import { esusApi } from "../api";
import { BasePatient } from "@/@types/BasePatient";
import { Attendance } from "@/@types/esus/Attendance";
import { DentalAttendance } from "@/@types/esus/DentalAttendance";
import { AttendanceProcedureReqEval } from "@/@types/esus/AttendanceProcedureReqEval";
import { Vaccination } from "@/@types/esus/Vaccination";
import { Problem } from "@/@types/esus/Problem";
import { WeightClassificationEnum } from "@/utils/enums/WeightClassificationEnum";
import { RacialColorEnum } from "@/utils/enums/RacialColorEnum";
import { PregnancyTypeEnum } from "@/utils/enums/PregnancyTypeEnum";
import { Cbo } from "@/@types/esus/Cbo";
import { Period } from "@/@types/generics/Period";
import { Paginated } from "@/@types/generics/paginated";
import { HealthTeam } from "@/@types/esus/HealthTeam";
import { HealthUnit } from "@/@types/esus/HealthUnit";
import { Cid } from "@/@types/esus/Cid";
import { Ciap } from "@/@types/esus/Ciap";
import { TScheduleStatus } from "@/@types/esus/TScheduleStatus";
import { Professional } from "@/@types/esus/Professional";
import { Shift } from "@/@types/esus/Shift";
import { Procedure } from "@/@types/esus/Procedure";
import { Specialty } from "@/@types/esus/Specialty";

export type ResumeGrouped<T, K> = {
	key: T;
	total: number;
	values?: K[];
};

export type AttendancePerformanceDashboard = {
	attendancesAggregatedByAgeAndGender?: ResumeGrouped<string, ResumeGrouped<string, object>>[];
	attendancesAggregatedByHealthTeamAndProfessionalCategory?: ResumeGrouped<
		HealthTeam,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByHealthUnitAndProfessionalCategory?: ResumeGrouped<
		HealthUnit,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByProfessionalCategoryAndProfessional?: ResumeGrouped<
		string,
		ResumeGrouped<Professional, object>
	>[];
	attendancesAggregatedByShift?: ResumeGrouped<Shift, object>[];

	attendanceProceduresAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendanceProceduresAggregatedByProcedureAndProfessionalCategory?: ResumeGrouped<
		Procedure,
		ResumeGrouped<string, object>
	>[];

	attendanceProcedureRequestedsAggregated?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendanceProcedureEvaluatedsAggregated?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];

	problemsAggregatedByCidAndProfessionalCategory?: ResumeGrouped<
		Cid,
		ResumeGrouped<string, object>
	>[];
	problemsAggregatedByCiapAndProfessionalCategory?: ResumeGrouped<
		Ciap,
		ResumeGrouped<string, object>
	>[];

	attendanceReferralsAggregatedByCiapAndProfessionalCategory?: ResumeGrouped<
		Ciap,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedByCidAndProfessionalCategory?: ResumeGrouped<
		Cid,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedBySpecialty?: ResumeGrouped<Specialty, object>[];

	schedulesAggregatedByPeriodAndScheduleStatus?: ResumeGrouped<
		Period,
		ResumeGrouped<TScheduleStatus, object>
	>[];
};

export type AttendanceDashboard = {
	attendancesAggregatedByAgeAndGender?: ResumeGrouped<string, ResumeGrouped<string, object>>[];
	attendancesAggregatedByHealthTeamAndProfessionalCategory?: ResumeGrouped<
		HealthTeam,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByHealthUnitAndProfessionalCategory?: ResumeGrouped<
		HealthUnit,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendancesAggregatedByProfessionalCategoryAndProfessional?: ResumeGrouped<
		string,
		ResumeGrouped<Professional, object>
	>[];
	attendancesAggregatedByShift?: ResumeGrouped<Shift, object>[];
};

export type AttendanceReferralDashboard = {
	attendanceReferralsAggregatedByCiapAndProfessionalCategory?: ResumeGrouped<
		Ciap,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedByCidAndProfessionalCategory?: ResumeGrouped<
		Cid,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendanceReferralsAggregatedBySpecialty?: ResumeGrouped<Specialty, object>[];
};

export type AttendanceProcedureDashboard = {
	attendanceProceduresAggregatedByPeriodAndProfessionalCategory?: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	attendanceProceduresAggregatedByProcedureAndProfessionalCategory?: ResumeGrouped<
		Procedure,
		ResumeGrouped<string, object>
	>[];
};

export type AttendanceProcedureReqEvalDashboard = {
	attendanceProcedureReqEvalsAggregated?: ResumeGrouped<Period, ResumeGrouped<string, object>>[];
};

export type ProblemDashboard = {
	problemsAggregatedByCidAndProfessionalCategory?: ResumeGrouped<
		Cid,
		ResumeGrouped<string, object>
	>[];
	problemsAggregatedByCiapAndProfessionalCategory?: ResumeGrouped<
		Ciap,
		ResumeGrouped<string, object>
	>[];
};

export type TScheduleDashboard = {
	schedulesAggregatedByPeriodAndScheduleStatus?: ResumeGrouped<
		Period,
		ResumeGrouped<TScheduleStatus, object>
	>[];
};

export type ResumePregnant = {
	resumeChildBirthType: ResumeGrouped<string | null, object>[];
	resumePregnancyStatus: ResumeGrouped<string | null, object>[];
	resumePuerperalStatus: ResumeGrouped<string | null, object>[];
	resumeGestationalAge: ResumeGrouped<string | null, object>[];
	resumeGestationalAgeTrimester: ResumeGrouped<number | null, object>[];
	resumeHivAndSifilisStatus: ResumeGrouped<string | null, object>[];
	resumeRacialColor: ResumeGrouped<RacialColorEnum | null, object>[];
	resumeHighRisk: ResumeGrouped<number | null, object>[];
	resumeChildBirthByPeriodAndType: ResumeGrouped<
		Period | null,
		ResumeGrouped<string | null, object>[]
	>[];
	averageGestationalAge: number;
	averagePregnantAge: number;
};

export type Pregnancy = BasePatient & {
	socialName: string;
	firstDumDate: string;
	firstAttendanceDate: string;
	dppDate: string;
	gender: number;
	inPreliminaryReport: boolean;
	preliminaryReportStatus: number;
	phone?: string;
	alcoholicStatus?: number;
	smokerStatus?: number;
	haveDiabetes?: number;
	haveHighBloodPressure?: number;
	lastIndividualRecordDate?: string;
	attendances: Attendance[];
	dentalAttendances?: DentalAttendance[];
	childBirthDate: string;
	puerperalAttendanceProcedures: AttendanceProcedureReqEval[];
	pregnancyProcedures?: AttendanceProcedureReqEval[];
	pregnancyVaccinations?: Vaccination[];
	childBirthType?:
		| "normal"
		| "cesarean"
		| "normalstillbirth"
		| "cesareanstillbirth"
		| "abortion"
		| "unspecified"
		| "unregistered";
	childBirthProblem?: Problem;
	attendanceStatus: string;
	puerperalStatus: string;
	pregnancyStatus: string;
	highRiskStatus?: number;
	plannedPregnancyStatus?: number;
	weightClassification?: WeightClassificationEnum;
	racialColor?: RacialColorEnum;
	pregnancyType?: PregnancyTypeEnum;
	firstGestationalAge?: number;
	lastGestationalAge?: number;
	citizenCbo?: Cbo;
};

type AttendanceDashboardParams = {
	uf: string;
	ibgeCode: string;
	cnes: string;
	cns?: string;
	ine?: string;
	startDate: string;
	endDate: string;
};

type EpidemicDashboardParams = {
	uf: string;
	ibgeCode: string;
	startDate: string;
	endDate: string;
	epidemicType: string;
};

type PregnantsWithChildBirthParams = {
	startDate: string;
	endDate: string;
	cnes?: string;
	ine?: string;
	fullName?: string;
	ibgeCode: string;
	pageNumber: number;
	pageSize?: number;
	uf: string;
	orderBy: string;
};

type ResumePregnantsWithChildBirthParams = {
	startDate: string;
	endDate: string;
	cnes?: string;
	ine?: string;
	ibgeCode: string;
	uf: string;
};

export async function getAttendancePerformanceDashboard({
	uf,
	ibgeCode,
	...params
}: AttendanceDashboardParams) {
	const response = await esusApi.get<AttendancePerformanceDashboard>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/GetAttendancePerformanceDashboard`,
		params
	);
	return response.data || ({} as AttendancePerformanceDashboard);
}

export async function getAttendanceDashboard({
	uf,
	ibgeCode,
	...params
}: AttendanceDashboardParams) {
	const response = await esusApi.get<AttendanceDashboard>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/GetDashboard`,
		params
	);
	return response.data || ({} as AttendanceDashboard);
}

export async function getEpidemicDashboard({ uf, ibgeCode, ...params }: EpidemicDashboardParams) {
	const response = await esusApi.get<AttendanceDashboard>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/GetEpidemicDashboard`,
		params
	);
	return response.data || ({} as AttendanceDashboard);
}

export async function getPregnantsWithChildBirthPaginated({
	uf,
	ibgeCode,
	...params
}: PregnantsWithChildBirthParams) {
	const response = await esusApi.get<Paginated<Pregnancy>>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/GetPregnantsWithChildBirthPaginated`,
		params
	);
	return response.data || ({} as Paginated<Pregnancy>);
}

export async function getPregnantsWithChildBirth({
	uf,
	ibgeCode,
	...params
}: Omit<PregnantsWithChildBirthParams, "pageNumber">) {
	const response = await esusApi.get<Pregnancy[]>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/GetPregnantsWithChildBirth`,
		params
	);
	return response.data || [];
}

export async function getResumePregnantsWithChildBirth({
	uf,
	ibgeCode,
	...params
}: ResumePregnantsWithChildBirthParams) {
	const response = await esusApi.get<ResumePregnant>(
		`/uf/${uf}/city/${ibgeCode}/Attendance/ResumePregnantsWithChildBirth`,
		params
	);
	return response.data || ({} as ResumePregnant);
}
